import React, { useEffect, useState } from "react";
import Offcanvas from "../../../Components/OffCanvas";
import ToggleSwitch from "../../../Components/ToggleSwitch";
import ImageComparison from "./ImageComparison";
import InspectedImages from "./InspectedImages";
import NewRelease from "./NewRelease";
import { NetworkManager } from "../../../Components/NetworkManager";
import ToastManager from "../../../Components/ToastManager";
import GenericPopup from "../../../Components/GenericPopup";
import { CommonUtil } from "../../../utils/CommonUtil";
import { Events } from "../../Languages/Events";

const comparisonConfig: any = {
  Light: [
    { label: "Result", field: "result" },
    { label: "Count", field: "lightCount" },
    { label: "Type", field: "lightType" },
    { label: "Reflector Status", field: "reflectorStatus" },
    { label: "Reflector Count", field: "reflectorCount" },
    { label: "Description", field: "description" },
  ],
  Tyre: [
    { label: "Status", field: "tyreStatus" },
    { label: "Brand", field: "tyreBrand" },
    { label: "Size", field: "tyreSize" },
    { label: "Depth", field: "tyreDepth" },
    { label: "Condition", field: "tyreCondition" },
    { label: "Description", field: "description" },
  ],
  Tire: [
    { label: "Status", field: "tyreStatus" },
    { label: "Brand", field: "tyreBrand" },
    { label: "Size", field: "tyreSize" },
    { label: "Depth", field: "tyreDepth" },
    { label: "Condition", field: "tyreCondition" },
    { label: "Description", field: "description" },
  ],
  Reefer: [
    { label: "Result", field: "result" },
    { label: "Description", field: "description" },
    { label: "Present Value", field: "presetValue" },
    { label: "Hour Value", field: "hoursValue" },
    { label: "Temperature Value", field: "tempratureValue" },
  ],
  Picture: [
    { label: "Description", field: "description" },
  ],
  General: [
    { label: "Result", field: "result" },
    { label: "Description", field: "description" },
  ],
  CTPAT: [
    { label: "Result", field: "result" },
    { label: "Description", field: "description" },
  ],
  Default: [
    { label: "Result", field: "result" },
  ],
};

const InspectedDetails = ({ data, getDocInspection }: any) => {
  const saasOrgId = CommonUtil.getSaasOrgId();
  const [showMismatchOnly, setShowMismatchOnly] = useState(false);
  const [galleryPop, setGalleryPop] = useState<boolean>(false);
  const [imagePop, setImagePop] = useState<boolean>(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [selectedProcessId, setSelectedProcessId] = useState<number | null>(null);
  const [selectedTasks, setSelectedTasks] = useState<any>([]);
  const [imagesData, setImagesData] = useState<any>([]);
  const documents = data;
  const isDifferentDocs = data?.[0]?.isToCompareDifferentDocs === true;
  const [filteredDocuments, setFilteredDocuments] = useState<Document[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  const [imageCount, setImageCount] = useState<number>(0);
  const [popupInfo, setPopupInfo] = useState<any>(null);
  const [releasePopupSize, setReleasePopupSize] = useState("35%");
  const [isDownloading, setIsDownloading] = useState(false);
  
  let onSwitchCustomer = () => {
      Events.dispatchBreadCrumbEvent();
  }

  useEffect(() => {
    document.addEventListener(Events.SWITCH_CUSTOMER_EVENT, onSwitchCustomer);
    return () => {
        document.removeEventListener(Events.SWITCH_CUSTOMER_EVENT, onSwitchCustomer);
    }
  }, []);

  useEffect(() => {
    if (documents?.length && selectedDocuments.length === 0) {
      documentImages(documents)
      const preSelected = isDifferentDocs
        ? documents.map((doc: any) => doc.documentId)
        : documents.map((doc: any) => doc.multiReleaseId);

      setSelectedDocuments(preSelected);
    }
  }, [documents, isDifferentDocs, selectedDocuments.length]);

 
  const documentImages = (data: any[]) => {
    const images = data?.map(document => ({
      documentId: document.documentId,
      docTypeName: document.docTypeName || undefined,
      documentCode: document.documentCode,
      vinNumber: document?.vinNumber,
      brand: document?.brand,
      unitNumber: document?.unitNumber,
      licenseNumber: document?.licenseNumber,
      saasOrgId: document.saasOrgId,
      Images: document.processDetails?.flatMap((process: any) =>
        process.taskDetails?.flatMap((task: any) =>
          task?.images?.map((img: any) => ({img: img?.imagePath, comment: img?.comment})) || []
        )
      )
    }));

    const totalImageCount = images?.reduce((acc: number, doc: any) => {
      return acc + (doc.Images?.length || 0);
    }, 0);

    setImageCount(totalImageCount);
    setImagesData(images);
  };

  useEffect(() => {
    if (selectedDocuments?.length === 0 || selectedDocuments?.length === 1) {
      setShowMismatchOnly(false);
    }
  }, [selectedDocuments])

  const documentOptions = documents?.map((doc: any) => ({
    value: isDifferentDocs ? doc.documentId : doc?.multiReleaseId,
    label: isDifferentDocs ? "#"+doc.documentCode : doc?.docTypeName,
  }));

  const selectedDocs = documents?.filter((doc: any) =>
    selectedDocuments.includes(isDifferentDocs ? doc.documentId : doc?.multiReleaseId)
  );
  
  useEffect(() => {
    if (!selectedProcessId || selectedDocs?.length === 0) return;

    const tasksPerDoc = getTasksForProcess(selectedProcessId);
    const grouped = groupTasksByType(tasksPerDoc);

    let needsUpdate = false;

    setExpandedSections((prev) => {
      const updated = { ...prev };
      Object.keys(grouped).forEach((type) => {
        if (!(type in updated)) {
          updated[type] = true;
          needsUpdate = true;
        }
      });
      return needsUpdate ? updated : prev;
    });
  }, [selectedProcessId, selectedDocs]);

  const commonProcesses = React.useMemo(() => {
    return selectedDocs?.length
      ? documents
          ?.find((doc: any) => (isDifferentDocs ? doc.documentId : doc?.multiReleaseId) === selectedDocuments[0])
          ?.processDetails?.filter((p: any) =>
            selectedDocs?.every((doc: any) =>
              doc.processDetails?.some((dp: any) => dp.processId === p.processId)
            )
          ) ?? []
      : [];
  }, [selectedDocs, documents]);

  useEffect(() => {
    if (
      commonProcesses?.length > 0 &&
      (!selectedProcessId || !commonProcesses.some((p: any) => p.processId === selectedProcessId))
    ) {
      const newProcessId = commonProcesses[0].processId;
      if (selectedProcessId !== newProcessId) {
        setSelectedProcessId(newProcessId);
      }
    }
  }, [commonProcesses, selectedProcessId]);

  const getTasksForProcess = (processId: any) => {
    return selectedDocs?.map((doc: any) => {
      const proc = doc.processDetails?.find(
        (p: any) => p.processId === processId
      );
      return proc?.taskDetails || [];
    });
  };

  const groupTasksByType = (taskLists: any) => {
    const grouped: any = {};

    taskLists.forEach((tasks: any, docIdx: any) => {
      tasks.forEach((task: any, taskIdx: any) => {
        const type = task.taskType || "Default";
        if (!grouped[type])
          grouped[type] = Array(selectedDocs?.length)
            ?.fill(null)
            ?.map(() => []);
        grouped[type][docIdx].push(task);
      });
    });

    return grouped;
  };

  const isMismatch = (type: string, tasks: any) => {
    const config = comparisonConfig[type] || comparisonConfig.Default;

    return config?.some(({ field }: any) => {
      const values = tasks?.map((t: any) => t?.[field]);
      return new Set(values).size > 1;
    });
  };
  
  const isLastColumnForDoc = (docIdx: number, fieldIdx: number, configLen: number) => {
    return fieldIdx === configLen - 1 && docIdx < selectedDocs?.length - 1;
  };

  const filterDocuments = (taskId: number): Document[] => {
    return documents
      ?.filter((doc: any) => selectedDocuments?.includes(isDifferentDocs ? doc?.documentId : doc?.multiReleaseId))
      ?.map((doc: any) => {
        const filteredProcessDetails = doc?.processDetails
          ?.filter((process: any) => process?.processId === selectedProcessId)
          ?.map((process: any) => {
            const filteredTaskDetails = process?.taskDetails
              ?.filter((task: any) => task?.taskId === taskId);
            
            return {
              ...process,
              taskDetails: filteredTaskDetails
            };
          })
          .filter((process: any) => process?.taskDetails.length > 0);

        return {
          ...doc,
          processDetails: filteredProcessDetails
        };
      })
      ?.filter((doc: any) => doc?.processDetails?.length > 0);
  };

  const renderComparisonTable = (type: string, groupedTasks: any) => {
    const config = comparisonConfig[type] || comparisonConfig.Default;
    const rowCount = Math.max(...groupedTasks?.map((list: any) => list?.length));

    const rows = Array.from({ length: rowCount }, (_, i) => {
      const rowTasks = groupedTasks?.map((list: any) => list[i]);
      if (showMismatchOnly && !isMismatch(type, rowTasks)) return null;

    const mismatchFields = config
      ?.map(({ field }: any) => {
        const values = rowTasks?.map((t: any) => t?.[field]);
        return new Set(values).size > 1 ? field : null;
      })
      ?.filter(Boolean);

    const isRowMismatch = mismatchFields?.length > 0;

      return (
        <tr key={i}>
          <td className="task-name-data" style={{borderBottom: "none", minWidth: "100px", maxWidth: "300px", overflowWrap: "break-word"}}>{rowTasks[0]?.taskName ?? "—"}</td>
          {rowTasks?.map((task: any, docIdx: number) =>
            config?.map((c: any, fieldIdx: number) => {
              const isMismatch = mismatchFields.includes(c.field);
              const isLast = isLastColumnForDoc(docIdx, fieldIdx, config?.length);

              return (
                <td
                  key={`${docIdx}-${c.field}`}
                  style={{
                    ...(c.field === "description" ? { minWidth: "100px", maxWidth: "250px", overflowWrap: "break-word" } : {}),
                    ...(isMismatch && { color: "#F04438", backgroundColor: "#FFFBFA" }),
                    ...(isRowMismatch ? { borderBottom: "2px solid #F04438" } : { borderBottom: "none" }),
                    ...(isLast && { borderRight: "4px solid #F2F4F7" }),
                  }}
                >
                  {task?.[c.field]?.toString().trim() ? task[c.field] : "—"}
                </td>
              );
            })
          )}
          {type !== "CTPAT" && (<td
            className="picture-cell text-center"
            style={isRowMismatch ? { borderBottom: "2px solid red" } : { borderBottom: "none" }}
            onClick={() => {
              const totalImages = rowTasks?.reduce((sum: any, task: any) => sum + (task?.images?.length || 0), 0);
              if (totalImages > 0) {
                const filtered = filterDocuments(rowTasks?.[0]?.taskId);
                setFilteredDocuments(filtered);
                setSelectedTasks(rowTasks?.[0]);
                setGalleryPop(true);
              }
            }}
          >
            {(() => {
              const totalImages = rowTasks?.reduce((sum: any, task: any) => sum + (task?.images?.length || 0), 0);
              return totalImages > 0 ? (
                <span className="picture-icon" role="button">
                  <span className="position-relative">
                    <img
                      src="../../images/picture.svg"
                      alt="Task"
                      className="task-picture"
                    />
                    <span className="position-absolute top-0 mt-1 start-100 translate-middle badge rounded-pill bg-primary">
                      {totalImages}
                      <span className="visually-hidden">unread messages</span>
                    </span>
                  </span>
                </span>
              ) : ("—");
            })()}
          </td>)}
        </tr>
      );
    });

    const hasData = rows?.some((row) => row !== null);
    if (!hasData) return null;
    
    const toggleSection = (type: string) => {
      setExpandedSections(prev => ({ ...prev, [type]: !prev[type] }));
    };
    
    return (
      <div className="" key={type}>
        <div className="section-header" onClick={() => toggleSection(type)}>
          <span className='me-2'>{expandedSections[type] ? 
            (<i className="fa-solid fa-chevron-down"></i>) : (<i className="fa-solid fa-chevron-right"></i>)}
          </span>
          <h3>{type}</h3>
        </div>
        {expandedSections[type] &&
        <div className="table-responsive comparison-table border border-1 ms-1">
          <table className="table mb-0 border border-1">
            <thead className="thead-light">
              <tr className="">
                <th className="task-name-data" style={{color: "#667085"}} rowSpan={2}>Task Name</th>
                  {selectedDocs?.map((doc: any, docIdx: number) => (
                  <th
                    key={isDifferentDocs ? doc.documentId : doc?.multiReleaseId}
                    colSpan={config?.length}
                    style={
                      docIdx < selectedDocs?.length - 1
                        ? { borderRight: "4px solid #F2F4F7" }
                        : undefined
                    }
                  >
                    <b>{isDifferentDocs ? "#"+doc.documentCode : doc?.docTypeName}</b>
                  </th>
                ))}
                {type !== "CTPAT" && (
                  <th className="picture-cell" rowSpan={2}>Picture</th>
                )}
              </tr>
              <tr>
                {selectedDocs?.map((doc: any, docIdx: number) =>
                  config?.map((c: any, fieldIdx: number) => (
                    <th key={`${isDifferentDocs ? doc.documentId : doc?.multiReleaseId}-${c.field}`}
                      style={{
                        ...(c.field === "description" ? { minWidth: "100px", maxWidth: "250px" } : {}),
                        ...(isLastColumnForDoc(docIdx, fieldIdx, config?.length)
                          ? { borderRight: "4px solid #F2F4F7" }
                          : {}),
                      }}
                    >
                      {c.label}
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </table>
        </div>}
      </div>
    );
  };

  const renderTables = () => {
    if (!selectedProcessId) return null;

    const tasksPerDoc = getTasksForProcess(selectedProcessId);
    const grouped = groupTasksByType(tasksPerDoc);

    return Object.entries(grouped)?.map(([type, group]) =>
      renderComparisonTable(type, group)
    );
  };

  const toggleDocumentSelection = (docId: number) => {
    setSelectedDocuments((prev) =>
      prev.includes(docId)
        ? prev?.filter((id) => id !== docId)
        : [...prev, docId]
    );
  };

  const OnReportDownload = async () => {
    if (isDownloading) return;

    setIsDownloading(true);

    const url = `api/V2InspectionDocuments/inspection-pdf/generate?docId=${CommonUtil.getInterchangeDocumentId()}&orgId=${saasOrgId}&dependentDocTypeId=${data?.[0].docTypeId}`;

    let myHeaders = new Headers();
    myHeaders?.append('Content-Type', 'application/json');

    if (localStorage.getItem('token')) {
      const token = 'Bearer ' + localStorage.getItem('token');
      myHeaders?.append('Authorization', token);
    }
    try {
      const response = await fetch(NetworkManager.serverPath + url, {
        method: 'POST',
        headers: myHeaders,
        // body: JSON.stringify(selectedImages),
      });
      
      if (!response.ok) {
        throw new Error('Download failed with status ' + response.status);
      } 

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${data?.[0]?.vinNumber}_${data?.[0]?.docTypeName}_Report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      ToastManager.showToast(`Report has been downloaded successfully as PDF`, "Info");
    } catch (error) {
      console.error('Download error:', error);
      ToastManager.showToast("Download Failed", "Error");
    } finally {
      setIsDownloading(false);
    }
  };

  const Loader = () => (
    <div className="">
      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        Downloading Report...
    </div>
  );
  
  return (
    <>
      <div className="container-fluid p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex">
            <h5 className="me-4"><span>{documents?.[0]?.documentCode}</span></h5>
            <span className='mt-1 ms-0 me-3' >Date:{CommonUtil.convertUtcToLocal(data?.[0]?.startTime)}</span>
            <span className='mx-4 mt-1'>Vin Number: {documents?.[0]?.vinNumber}</span>
            <div className="d-flex align-items-center mt-0 ms-4 mb-1">
              <img src="../../images/download-lite.svg" alt="download" style={{width: "13px", marginTop: "2px"}} />
              <span className="mx-1" role="button" onClick={()=>{setImagePop(true)}} style={{textDecoration: "none", color:"#4C80CA"}}>
                <u><b>Download / Share</b></u>
              </span>{imageCount} Pictures</div>
            <div className="d-flex align-items-center mt-0 ms-5 mb-1">
              <img src="../../images/download-lite.svg" alt="download" style={{width: "13px", marginTop: "2px"}} />
              <span className="mx-1" role="button" onClick={OnReportDownload} style={{textDecoration: "none", color:"#4C80CA"}}>
                <u><b>Download Report</b></u>
              </span>
            </div>
          </div>
          {documents?.[0]?.isInterchnageEnabled === true && documents?.[0]?.processDetails?.length > 0 && 
          <div className='d-flex justify-content-end'>
            <button className='btn-green mx-3' onClick={() => {setPopupInfo({type: "AddNewRelease"});}}>
              <i className="fa-solid fa-plus pe-1"></i>Add New Release</button>
          </div>}
        </div>
        <div>
          <div className="d-flex justify-content-start align-items-start">          
            <div className="ms-1 me-4 mb-3" style={{maxWidth: "20%"}}>
              <label className="form-label mb-0">{isDifferentDocs ? "Documents" : "Documents Types"}</label>
              <div className="d-flex align-items-center mt-2">          
                <div className="d-flex flex-wrap">
                  {documentOptions?.map((opt: any) => (
                    <button
                      key={opt.value}
                      className={`document-tab g-2 ${selectedDocuments.includes(opt.value) ? 'selected' : ''}`}
                      onClick={() => toggleDocumentSelection(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{maxWidth: "62%"}}>
              {commonProcesses?.length > 0 && (    
                <div className="ms-1 mb-3">
                    <label className="form-label mb-0">Process </label>
                  <div className="d-flex align-items-center mt-2">             
                    <div className="d-flex flex-wrap">
                      {commonProcesses?.map((process: any) => (
                        <button
                          key={process.processId}
                          className={`process-tab g-2 ${selectedProcessId === process.processId ? 'selected' : ''}`}
                          onClick={() => setSelectedProcessId(process.processId)}
                        >
                          {process.processName}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                )}
            </div>
            <div className="ms-3" style={{marginTop: "28px"}}>
            <div className="d-flex justify-content-start align-items-start mt-0 ms-1">
              <div className="text-start ms-1 mt-0" style={{width: "240px"}}>
                {selectedDocuments?.length > 1 && selectedProcessId !== null && (
                  <div className="">
                    <ToggleSwitch
                      checked={showMismatchOnly}
                      onChange={() => setShowMismatchOnly(!showMismatchOnly)}
                      label="Show Mismatches Only"
                      labelStyle="ms-2 fw-600"
                    />
                  </div>
                )}
              </div>
            </div>
            </div>
          </div>
        </div>
        {renderTables()}
      </div>
      <Offcanvas 
        show={galleryPop}
        onHide={() => {
          setGalleryPop(false);
          setFilteredDocuments([]);
          setSelectedTasks(null);
        }}
        title={selectedTasks?.taskName}
        size="60%"
      >
        {selectedProcessId !== null && (
          <ImageComparison data={filteredDocuments} isDifferentDocs={isDifferentDocs}/>
        )}
      </Offcanvas>
      <Offcanvas 
        show={imagePop}
        onHide={() => {
          setImagePop(false);
        }}
        title="Download / Share Pictures"
        size="70%"
      >
        <InspectedImages data={imagesData} imageCount={imageCount} show={imagePop} />
      </Offcanvas>
      <Offcanvas 
        show={popupInfo?.type === "AddNewRelease"}
        onHide={() => setPopupInfo(null)}
        title="Add New Release"
        size={releasePopupSize}
      >
        <NewRelease
          show={popupInfo?.type === "AddNewRelease"}
          data={documents}
          onClose={() => {setPopupInfo(null); getDocInspection();}}
          popupSize={(percent: number) => setReleasePopupSize(`${percent}%`)} 
        />
      </Offcanvas>
      {isDownloading && (
        <GenericPopup
          mode="Share"
          onClose={() => console.log("")}
          size="sm"
          action={false}
        ><Loader/>
        </GenericPopup>
      )}
    </>
  );
};

export default InspectedDetails;
